/**
 * Calendar-date helpers over `YYYY-MM-DD` strings.
 *
 * All arithmetic goes through UTC midnight so it never picks up a DST offset — the
 * old code mixed `Date` objects and strings and compared them with `<`, which is the
 * ambiguity this module exists to remove.
 */

export type DateStr = string;

const DAY_MS = 86_400_000;

export function isDateStr(v: unknown): v is DateStr {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

export function toUtc(date: DateStr): Date {
  return new Date(`${date}T00:00:00.000Z`);
}

export function fromUtc(d: Date): DateStr {
  return d.toISOString().slice(0, 10);
}

export function addDays(date: DateStr, days: number): DateStr {
  return fromUtc(new Date(toUtc(date).getTime() + days * DAY_MS));
}

export function nextDay(date: DateStr): DateStr {
  return addDays(date, 1);
}

export function daysBetween(from: DateStr, to: DateStr): number {
  return Math.round((toUtc(to).getTime() - toUtc(from).getTime()) / DAY_MS);
}

/** Both are plain `YYYY-MM-DD`, so lexicographic order is chronological order. */
export function maxDate(...dates: (DateStr | undefined)[]): DateStr {
  const present = dates.filter(isDateStr);
  if (present.length === 0) throw new Error('maxDate: no dates given');
  return present.reduce((a, b) => (b > a ? b : a));
}

export function minDate(...dates: (DateStr | undefined)[]): DateStr {
  const present = dates.filter(isDateStr);
  if (present.length === 0) throw new Error('minDate: no dates given');
  return present.reduce((a, b) => (b < a ? b : a));
}

/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date: DateStr): number {
  return toUtc(date).getUTCDay();
}

export function isWeekend(date: DateStr): boolean {
  const d = dayOfWeek(date);
  return d === 0 || d === 6;
}

export function eachDay(from: DateStr, to: DateStr): DateStr[] {
  const out: DateStr[] = [];
  for (let d = from; d <= to; d = nextDay(d)) out.push(d);
  return out;
}

/** Monday of the ISO week containing `date`. */
export function startOfWeek(date: DateStr): DateStr {
  const dow = dayOfWeek(date);
  return addDays(date, dow === 0 ? -6 : 1 - dow);
}

/** Today's calendar date in the shop's timezone (SPEC §7 — no UTC/local drift). */
export function todayIn(timeZone: string, now: Date = new Date()): DateStr {
  // `en-CA` renders as YYYY-MM-DD, which is exactly the wire format.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/** The shop-local hour of day, 0–23, for `now`. */
export function hourIn(timeZone: string, now: Date = new Date()): number {
  return Number(
    new Intl.DateTimeFormat('en-CA', { timeZone, hour: 'numeric', hourCycle: 'h23' }).format(now),
  );
}

/**
 * The earliest day new, unpinned scheduling hours may land — ordinarily today, but
 * past `cutoffHour` there is too little of the working day left to actually use it, so
 * this defers to tomorrow instead. Already-pinned or already-completed work sitting on
 * today is untouched either way; this only decides where the *next* hour goes.
 */
export function planningFloor(
  timeZone: string,
  rule: { enabled: boolean; cutoffHour: number },
  now: Date = new Date(),
): DateStr {
  const today = todayIn(timeZone, now);
  if (!rule.enabled) return today;
  return hourIn(timeZone, now) >= rule.cutoffHour ? addDays(today, 1) : today;
}

/** First day of the month containing `date`. Dates are `YYYY-MM-DD`, so this is a slice. */
export function startOfMonth(date: DateStr): DateStr {
  return `${date.slice(0, 7)}-01`;
}

export function daysInMonth(date: DateStr): number {
  const d = toUtc(startOfMonth(date));
  // Day 0 of the next month is the last day of this one.
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}

export function endOfMonth(date: DateStr): DateStr {
  return `${date.slice(0, 7)}-${String(daysInMonth(date)).padStart(2, '0')}`;
}

/**
 * The same day of the month, `months` later or earlier, clamped to the last day of the
 * target month — so paging a calendar off 31 January lands on 28 February rather than
 * skidding into March, which is what plain day arithmetic would do.
 */
export function addMonths(date: DateStr, months: number): DateStr {
  const d = toUtc(date);
  const first = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + months, 1));
  const key = first.toISOString().slice(0, 7);
  const day = Math.min(Number(date.slice(8, 10)), daysInMonth(`${key}-01`));
  return `${key}-${String(day).padStart(2, '0')}`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** `Tue 12 Aug` — display only. */
export function formatDayShort(date: DateStr): string {
  const d = toUtc(date);
  return `${DOW[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
}

/** `12 Aug 2026` — display only. */
export function formatDate(date: DateStr): string {
  const d = toUtc(date);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** `August 2026` — display only, for a calendar heading. */
export function formatMonth(date: DateStr): string {
  const d = toUtc(date);
  return `${MONTHS_LONG[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}
